/**
 * The daily checklist (§6.5, §9.5, NFR-2).
 *
 * Two minutes, on a cheap Android phone, standing in a shed. Everything here
 * follows from that:
 *
 *   Three big toggles rather than a form. The whole checklist is answerable
 *   with three thumb taps, and nothing is required.
 *
 *   The draft survives leaving the page. A partner gets interrupted, or the
 *   browser drops the tab to reclaim memory — which cheap phones do
 *   aggressively — and everything typed would otherwise be gone. After that
 *   happens twice the form gets filled in from memory at the end of the day,
 *   which is worse information than a partial record made at the time.
 *
 *   Saving does not clear the screen. A second submission corrects the same
 *   day rather than creating a new one, so the partner can add the meter
 *   reading at night without losing the morning's checklist.
 */

import { useEffect, useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { todayInDhaka } from '../../lib/date.js';
import { clearDraft, clearStaleDrafts, draftKey, readDraft, writeDraft } from '../../lib/draft.js';
import { useSaveDailyLog } from './api.js';

interface Draft {
  cleaningDone: boolean;
  waterChecked: boolean;
  animalsInspected: boolean;
  electricityMeterReading: string;
  note: string;
}

const EMPTY: Draft = {
  cleaningDone: false,
  waterChecked: false,
  animalsInspected: false,
  electricityMeterReading: '',
  note: '',
};

const CHECKS = [
  { field: 'cleaningDone', key: 'daily.cleaning' },
  { field: 'waterChecked', key: 'daily.water' },
  { field: 'animalsInspected', key: 'daily.inspected' },
] as const;

export function DailyLogForm(): JSX.Element {
  const { t } = useTranslation();
  const save = useSaveDailyLog();

  const date = todayInDhaka();
  const key = draftKey('daily-log', date);

  // Restored on mount, before the first paint the partner sees.
  const [draft, setDraft] = useState<Draft>(() => ({ ...EMPTY, ...(readDraft<Partial<Draft>>(key) ?? {}) }));
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    // Yesterday's draft is dead weight; the day-scoped key makes it findable.
    clearStaleDrafts('daily-log', date);
  }, [date]);

  useEffect(() => {
    writeDraft(key, draft);
  }, [key, draft]);

  const set = <K extends keyof Draft>(field: K, value: Draft[K]): void => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrorKey(null);
  };

  async function handleSubmit(): Promise<void> {
    setErrorKey(null);

    try {
      await save.mutateAsync({
        date,
        cleaningDone: draft.cleaningDone,
        waterChecked: draft.waterChecked,
        animalsInspected: draft.animalsInspected,
        ...(draft.electricityMeterReading.trim() !== ''
          ? { electricityMeterReading: draft.electricityMeterReading.trim() }
          : {}),
        ...(draft.note.trim() !== '' ? { note: draft.note.trim() } : {}),
      });

      // The draft has served its purpose: the server has it now, and a stale
      // draft reappearing over saved data is worse than losing the draft.
      clearDraft(key);
      setSavedAt(new Date().toISOString());
    } catch (error) {
      // Everything stays on screen, and the draft is still in storage.
      setErrorKey(errorMessageKey(error));
    }
  }

  const done = CHECKS.filter((check) => draft[check.field]).length;

  return (
    <form
      className="page-container"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <div className="flex items-baseline justify-between py-4">
        <h1 className="text-xl font-semibold text-gray-800">{t('daily.title')}</h1>
        <span className="text-sm text-gray-500">{t('daily.progress', { done, total: CHECKS.length })}</span>
      </div>

      <div className="mb-5 space-y-2">
        {CHECKS.map((check) => (
          <button
            key={check.field}
            type="button"
            role="switch"
            aria-checked={draft[check.field]}
            onClick={() => set(check.field, !draft[check.field])}
            className={`flex min-h-[56px] w-full items-center justify-between rounded-xl px-4 py-3 text-left ${
              draft[check.field] ? 'bg-green-700 text-white' : 'bg-white text-gray-800 shadow-sm'
            }`}
          >
            <span className="font-medium">{t(check.key)}</span>
            <span aria-hidden="true" className="text-xl">
              {draft[check.field] ? '✓' : '○'}
            </span>
          </button>
        ))}
      </div>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="meter">
        {t('daily.meter')}
      </label>
      <input
        id="meter"
        inputMode="decimal"
        placeholder="0.0"
        value={draft.electricityMeterReading}
        onChange={(event) => set('electricityMeterReading', event.target.value)}
        className="mb-4 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3 text-right tabular-nums"
      />

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="dailyNote">
        {t('daily.note')}
      </label>
      <input
        id="dailyNote"
        value={draft.note}
        onChange={(event) => set('note', event.target.value)}
        maxLength={2000}
        className="mb-5 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />

      {errorKey !== null && (
        <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      {savedAt !== null && errorKey === null && (
        <p role="status" className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          {t('daily.saved')}
        </p>
      )}

      <button
        type="submit"
        disabled={save.isPending}
        className="min-h-[52px] w-full rounded-xl bg-green-700 text-lg font-medium text-white disabled:opacity-50"
      >
        {t(save.isPending ? 'daily.saving' : 'daily.submit')}
      </button>
    </form>
  );
}
